import { FaqItem } from '../services/faqService';

/**
 * Normalizes a string for search comparison by:
 * - Converting to lowercase
 * - Removing diacritics (accents)
 */
export function normalizeSearchText(value: string): string {
    return value
        .toLowerCase()
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '');
}

export interface SearchFaqItemsOptions {
    /**
     * Whether to include category in the search blob.
     * Default: false (only searches question and answer)
     */
    includeCategory?: boolean;
    /**
     * Whether to sort results by category then question.
     * Default: false (maintains original order)
     */
    sortByCategory?: boolean;
}

/**
 * Searches FAQ items based on search text.
 * Uses token-based matching where all tokens must be found in the searchable text.
 *
 * @param items - Array of FAQ items to search
 * @param searchText - Search query text
 * @param options - Search options
 * @returns Filtered array of FAQ items matching the search
 */
export function searchFaqItems(
    items: FaqItem[],
    searchText: string,
    options: SearchFaqItemsOptions = {}
): FaqItem[] {
    const { includeCategory = false, sortByCategory = false } = options;

    // Return all items if search is empty
    if (!searchText.trim()) {
        return items;
    }

    // Split search text into tokens and normalize
    const tokens = normalizeSearchText(searchText)
        .split(/\s+/)
        .filter(Boolean);

    // Filter items where all tokens are found
    const result = items.filter((item) => {
        // Build searchable blob based on options
        const searchableText = includeCategory
            ? `${item.category} ${item.question} ${item.answer}`
            : `${item.question} ${item.answer}`;
        
        const blob = normalizeSearchText(searchableText);
        return tokens.every((tok) => blob.includes(tok));
    });

    // Sort if requested
    if (sortByCategory) {
        return result.sort((a, b) => {
            const cat = a.category.localeCompare(b.category);
            if (cat !== 0) return cat;
            return a.question.localeCompare(b.question);
        });
    }

    return result;
}

